import { ArrowLeftIcon, ArrowRightIcon, EllipsisIcon, PencilIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
} from "~/components/ui/menu";

import { RenameDialog } from "./RenameDialog";
import { DISPLAY_LABELS, type DirectEdit, type WidgetChoices } from "./directEdits";

/**
 * The menu on a widget of a dashboard the viewer may edit: rename, show it as
 * another chart or a table, move it, change its width, remove it. Every choice
 * is a direct edit that saves a version, so Undo is always there.
 */
export function WidgetEditMenu(props: {
  choices: WidgetChoices;
  disabled: boolean;
  onEdit: (edit: DirectEdit) => void;
  title: string;
}) {
  const { choices } = props;
  const [renaming, setRenaming] = useState(false);

  return (
    <>
      <div className="absolute end-2 top-2 z-10 opacity-0 transition-opacity group-focus-within/widget:opacity-100 group-hover/widget:opacity-100 data-[popup-open]:opacity-100 max-md:opacity-100">
        <Menu>
          <MenuTrigger
            render={
              <Button
                aria-label={`Edit ${props.title}`}
                disabled={props.disabled}
                size="icon-xs"
                variant="ghost"
              />
            }
          >
            <EllipsisIcon aria-hidden />
          </MenuTrigger>
          <MenuPopup align="end" className="min-w-52">
            <MenuItem onClick={() => setRenaming(true)}>
              <PencilIcon aria-hidden />
              Rename
            </MenuItem>
            {choices.viewAs.length ? (
              <>
                <MenuSeparator />
                <MenuGroup>
                  <MenuGroupLabel>Show as</MenuGroupLabel>
                  {choices.viewAs.map((display) => (
                    <MenuItem
                      key={display}
                      onClick={() => props.onEdit({ display, kind: "view_as" })}
                    >
                      {DISPLAY_LABELS[display]}
                    </MenuItem>
                  ))}
                </MenuGroup>
              </>
            ) : null}
            {choices.canMoveEarlier || choices.canMoveLater ? (
              <>
                <MenuSeparator />
                {choices.canMoveEarlier ? (
                  <MenuItem onClick={() => props.onEdit({ direction: "earlier", kind: "move" })}>
                    <ArrowLeftIcon aria-hidden />
                    Move earlier
                  </MenuItem>
                ) : null}
                {choices.canMoveLater ? (
                  <MenuItem onClick={() => props.onEdit({ direction: "later", kind: "move" })}>
                    <ArrowRightIcon aria-hidden />
                    Move later
                  </MenuItem>
                ) : null}
              </>
            ) : null}
            {choices.widths.length ? (
              <>
                <MenuSeparator />
                <MenuGroup>
                  <MenuGroupLabel>Width</MenuGroupLabel>
                  {choices.widths.map((width) => (
                    <MenuItem
                      key={width.label}
                      onClick={() => props.onEdit({ kind: "width", span: width.span })}
                    >
                      {width.label}
                    </MenuItem>
                  ))}
                </MenuGroup>
              </>
            ) : null}
            {choices.canRemove ? (
              <>
                <MenuSeparator />
                <MenuItem variant="destructive" onClick={() => props.onEdit({ kind: "remove" })}>
                  <Trash2Icon aria-hidden />
                  Remove
                </MenuItem>
              </>
            ) : null}
          </MenuPopup>
        </Menu>
      </div>
      <RenameDialog
        currentName={props.title}
        heading="Rename this widget"
        inputLabel="Widget title"
        onOpenChange={setRenaming}
        onRename={(title) => props.onEdit({ kind: "rename", title })}
        open={renaming}
      />
    </>
  );
}
